import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

function getOrCreateSessionId() {
  let s = localStorage.getItem("rc_chat_session");
  if (!s) {
    s = "sess_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("rc_chat_session", s);
  }
  return s;
}

const WELCOME = {
  role: "assistant",
  content:
    "Hey there! 👋 I'm Nova, your Rocket Cap funding assistant. Ask me anything about our funding products, eligibility, or how to apply — I can also help pre-qualify you in a few questions.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(getOrCreateSessionId);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");

    const userMsg = { role: "user", content: text };
    const assistantIdx = messages.length + 1;
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[assistantIdx] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[assistantIdx] = {
          role: "assistant",
          content: "Sorry — I hit a snag. Please try again or use the contact form.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-4 md:right-6 z-[90] w-[min(94vw,380px)] h-[560px] max-h-[80vh] rounded-3xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          >
            <div className="bg-gradient-to-br from-[#0b1121] to-[#312e81] text-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 grid place-items-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-display font-bold">Nova · Funding Assistant</p>
                <p className="text-[11px] text-purple-200 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  Online — replies instantly
                </p>
              </div>
              <button
                data-testid="chat-close"
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50/50">
              {messages.map((m, i) => (
                <div
                  key={i}
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white rounded-br-sm"
                        : "bg-white border border-slate-100 text-slate-800 rounded-bl-sm"
                    }`}
                  >
                    {m.content || (streaming ? <TypingDots /> : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-slate-100 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKey}
                  placeholder="Ask about funding, rates, eligibility…"
                  className="flex-1 resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500 max-h-32"
                />
                <button
                  data-testid="chat-send"
                  onClick={send}
                  disabled={streaming || !input.trim()}
                  className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-600 to-fuchsia-600 disabled:opacity-40 text-white grid place-items-center shadow-lg shadow-purple-500/30"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="chat-fab"
        onClick={() => setOpen((v) => !v)}
        className={`fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[95] w-14 h-14 rounded-full grid place-items-center text-white shadow-2xl shadow-purple-500/40 transition-transform hover:scale-110 ${
          open
            ? "bg-slate-900"
            : "bg-gradient-to-br from-purple-600 to-fuchsia-600 pulse-ring"
        }`}
        aria-label="Open chat"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "300ms" }} />
    </span>
  );
}
